import type { MeResponse } from '@tvitvi/shared';
import { useRouterState } from '@tanstack/react-router';
import { useEffect, useState, type ReactNode } from 'react';

import { OnboardingGate } from './OnboardingGate';
import { MAIN_CONTENT_ID, SkipLink } from './SkipLink';
import { Sidebar } from './Sidebar';
import { BreadcrumbProvider } from './breadcrumbs';
import { Topbar } from './Topbar';

const SIDEBAR_ID = 'tvitvi-sidebar';

/**
 * The signed-in frame: a fixed dense sidebar, a 44 px topbar, and one scrolling content column.
 * Nothing about a page changes this frame — which is why a page can be swapped for a real one in
 * Phase 1 without touching the shell.
 */
export function AppShell({
  me,
  banner,
  children,
}: {
  me: MeResponse;
  /** Et bånd over hele skallet — gjestelinja (P10, funn D). Se `GuestBar`. */
  banner?: ReactNode;
  children: ReactNode;
}): ReactNode {
  /*
   * Skuffen husker STIEN den ble åpnet på, ikke bare «åpen». Et klikk på et menypunkt bytter sti,
   * og da er den lukket uten en effekt som må huske å lukke den.
   */
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const [navOpenedAt, setNavOpenedAt] = useState<string | null>(null);
  const narrowNavOpen = navOpenedAt === pathname;
  const closeNarrowNav = (): void => {
    setNavOpenedAt(null);
  };

  useEffect(() => {
    if (!narrowNavOpen) return;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setNavOpenedAt(null);
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, [narrowNavOpen]);

  return (
    /* Stien meldes fra sidene under og tegnes i toppen. Se `breadcrumbs.tsx`. */
    <BreadcrumbProvider>
      {/*
       * Glass 2 (§16.3): ingen bakgrunnsfarge her. Bakgrunnslaget er `body::before` i globals.css,
       * og sidemenyen og toppfeltet svever over det med luft rundt seg. Innholdet ruller helt ned
       * til kanten av vinduet, så det er ingen luft under `<main>`.
       */}
      <div className="flex h-dvh flex-col overflow-hidden">
        {banner}
        {/* B3 (253.26): telefonen som mangler er en stille linje øverst, ikke et vindu. */}
        {me.activeOrganizationId === null ? null : (
          <OnboardingGate organizationId={me.activeOrganizationId} />
        )}
        <div className="flex min-h-0 flex-1 gap-[var(--shell-gap)] overflow-hidden ps-[var(--shell-gap)] pt-[var(--shell-gap)]">
          <SkipLink />
          {/*
           * SMAL SKJERM (NAVIGATION.md, «Smal skjerm»; QA6 K2 funn 4). Under 900 px legger menyen seg
           * bak hamburgeren i toppfeltet og skyves inn som et overlegg. Før dette sto den med full
           * bredde på 390 px og presset innholdet ned til ~100 px — ett ord per linje.
           * Alt er `max-[899px]:`, så skrivebordet får nøyaktig de samme klassene som før.
           */}
          {narrowNavOpen ? (
            /*
             * Et klikk utenfor lukker, som en skuff. Ikke en kontroll: Escape og hamburgeren er
             * tastaturveien, og et usynlig felt i fokusrekkefølgen ville vært en knapp uten mening.
             */
            <div
              aria-hidden
              data-testid="nav-scrim"
              onClick={closeNarrowNav}
              className="fixed inset-0 z-40 bg-[color:var(--color-overlay)] min-[900px]:hidden"
            />
          ) : null}
          <Sidebar
            me={me}
            id={SIDEBAR_ID}
            className={
              narrowNavOpen
                ? 'max-[899px]:fixed max-[899px]:start-[var(--shell-gap)] max-[899px]:top-[var(--shell-gap)] max-[899px]:bottom-[var(--shell-gap)] max-[899px]:z-50 max-[899px]:mb-0 max-[899px]:max-w-[calc(100vw-var(--shell-gap)*2)] max-[899px]:bg-[var(--surface-popover)]'
                : 'max-[899px]:hidden'
            }
          />
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <Topbar
              me={me}
              nav={{
                open: narrowNavOpen,
                controls: SIDEBAR_ID,
                onToggle: () => {
                  setNavOpenedAt(narrowNavOpen ? null : pathname);
                },
              }}
            />
            {/*
             * `relative` is load-bearing, and it is not about anything you can see.
             *
             * The shell is `h-dvh overflow-hidden` with this as the only scroll region, so the
             * document must never scroll. It did, on `/settings/permissions`: 954px of phantom scroll
             * on top of this element's own 1614, giving two nested scrollbars, cutting `fullPage`
             * screenshots off mid-page, and making `scrollIntoView` unable to reach the bottom of the
             * page.
             *
             * The cause was `sr-only`, which is `position: absolute`. With no positioned ancestor its
             * containing block is the initial one — the document — so every visually hidden element
             * inside a long page lands at its static position in DOCUMENT coordinates and stretches
             * `html.scrollHeight` to reach it. The element defining the height turned out to be a
             * one-pixel `<caption class="sr-only">` at y=1853.
             *
             * It only showed on that page because it is the first one whose content is taller than
             * the viewport, so it is the first where those hidden elements fall below the fold. Every
             * later long page would have inherited it.
             *
             * `no-document-scroll.spec.ts` holds the rule now.
             */}
            <main id={MAIN_CONTENT_ID} tabIndex={-1} className="relative flex-1 overflow-y-auto">
              <div className="mx-auto w-full max-w-[var(--content-max)] px-5 py-5">{children}</div>
            </main>
          </div>
        </div>
      </div>
    </BreadcrumbProvider>
  );
}
